import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import mapboxgl from "mapbox-gl";

type OwnerToken = symbol;

type RegisteredMap = {
  map: mapboxgl.Map;
  ownerToken: OwnerToken;
};

type MapRegistryContextValue = {
  registerMap: (id: string, map: mapboxgl.Map, ownerToken: OwnerToken) => void;
  unregisterMap: (id: string, ownerToken: OwnerToken) => void;
  getMap: (id: string) => mapboxgl.Map | undefined;
};

const MapRegistryContext = createContext<MapRegistryContextValue | undefined>(
  undefined,
);

const MAP_DEBUG_ENABLED = import.meta.env.VITE_MAP_DEBUG === "1";

export function MapRegistryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const registryRef = useRef<Map<string, RegisteredMap>>(new Map());

  const registerMap = useCallback(
    (id: string, map: mapboxgl.Map, ownerToken: OwnerToken) => {
      const existing = registryRef.current.get(id);
      if (existing && existing.ownerToken !== ownerToken && MAP_DEBUG_ENABLED) {
        console.warn("[MapRegistry] replacing map owned by another surface", {
          id,
        });
      }

      registryRef.current.set(id, { map, ownerToken });
    },
    [],
  );

  const unregisterMap = useCallback((id: string, ownerToken: OwnerToken) => {
    const existing = registryRef.current.get(id);
    if (!existing || existing.ownerToken !== ownerToken) return;
    registryRef.current.delete(id);
  }, []);

  const getMap = useCallback((id: string) => {
    const existing = registryRef.current.get(id);
    return existing?.map;
  }, []);

  // Read-only lookup for E2E probes, so a test can assert what a surface
  // actually DREW rather than what the UI says it drew. MapView already
  // publishes its own map as __liveMap for this; going through the registry
  // covers every surface (the intrusion dialog's included) with one hook.
  useEffect(() => {
    const w = window as unknown as { __mapSurface?: (id: string) => mapboxgl.Map | undefined };
    w.__mapSurface = getMap;
    return () => {
      delete w.__mapSurface;
    };
  }, [getMap]);

  const value = useMemo<MapRegistryContextValue>(
    () => ({
      registerMap,
      unregisterMap,
      getMap,
    }),
    [registerMap, unregisterMap, getMap],
  );

  return (
    <MapRegistryContext.Provider value={value}>
      {children}
    </MapRegistryContext.Provider>
  );
}

export function useMapRegistry() {
  const context = useContext(MapRegistryContext);
  if (!context) {
    throw new Error("useMapRegistry must be used within a MapRegistryProvider");
  }
  return context;
}

export type { OwnerToken };
